<template>
  <form class="qualification-step" novalidate @submit.prevent="handleSubmit">
    <p class="step-eyebrow">{{ $t("billing.qualification.eyebrow") }}</p>
    <h2 class="font-serif mb-2">{{ $t("billing.qualification.title") }}</h2>
    <p class="text-body-secondary mb-4">
      {{ $t("billing.qualification.subtitle") }}
    </p>

    <!-- Locked while submitting, so no answer can be saved after the submitted ones. -->
    <fieldset
      class="mb-4"
      :disabled="loading"
      :aria-describedby="
        submitted && !consumptionBand ? `${formId}-band-error` : undefined
      "
      data-testid="consumption-question"
    >
      <legend class="question-label mb-2">
        {{ $t("billing.qualification.consumption_label") }}
      </legend>
      <div class="row g-2">
        <div v-for="band in CONSUMPTION_BANDS" :key="band" class="col-sm-6">
          <label
            class="choice-tile h-100"
            :class="{ 'is-selected': consumptionBand === band }"
            :data-testid="`band-${band}`"
          >
            <input
              v-model="consumptionBand"
              type="radio"
              class="visually-hidden"
              :name="`${formId}-band`"
              :value="band"
              @change="emitChange({ consumption_band: band })"
            />
            <span class="choice-check is-radio" aria-hidden="true"></span>
            <span class="d-block fw-bold">
              {{ $t(`billing.qualification.consumption_bands.${band}.name`) }}
            </span>
            <span class="d-block small text-body-secondary">
              {{
                $t(
                  `billing.qualification.consumption_bands.${band}.description`
                )
              }}
            </span>
          </label>
        </div>
      </div>
      <div
        v-if="submitted && !consumptionBand"
        :id="`${formId}-band-error`"
        class="invalid-feedback d-block"
        data-testid="band-required"
      >
        {{ $t("billing.qualification.required") }}
      </div>
    </fieldset>

    <fieldset
      class="mb-4"
      :disabled="loading"
      :aria-describedby="
        submitted && !countryScope ? `${formId}-scope-error` : undefined
      "
      data-testid="country-question"
    >
      <legend class="question-label mb-2">
        {{ $t("billing.qualification.countries_label") }}
      </legend>
      <div class="row g-2">
        <div v-for="scope in COUNTRY_SCOPES" :key="scope" class="col-sm-6">
          <label
            class="choice-tile h-100"
            :class="{ 'is-selected': countryScope === scope }"
            :data-testid="`scope-${scope}`"
          >
            <input
              v-model="countryScope"
              type="radio"
              class="visually-hidden"
              :name="`${formId}-scope`"
              :value="scope"
              @change="emitChange({ country_scope: scope })"
            />
            <span class="choice-check is-radio" aria-hidden="true"></span>
            <span class="d-block fw-bold">
              {{ $t(`billing.qualification.country_scopes.${scope}`) }}
            </span>
          </label>
        </div>
      </div>
      <div
        v-if="submitted && !countryScope"
        :id="`${formId}-scope-error`"
        class="invalid-feedback d-block"
        data-testid="scope-required"
      >
        {{ $t("billing.qualification.required") }}
      </div>
    </fieldset>

    <fieldset class="mb-4" :disabled="loading">
      <legend class="question-label mb-1">
        {{ $t("billing.qualification.standards_label") }}
      </legend>
      <p class="form-text mt-0 mb-2">
        {{ $t("billing.qualification.standards_optional") }}
      </p>
      <div class="row g-2">
        <div
          v-for="standard in REPORTING_STANDARDS"
          :key="standard"
          class="col-6 col-md-4"
        >
          <label
            class="choice-tile h-100"
            :class="{ 'is-selected': standards.includes(standard) }"
            :data-testid="`standard-${standard}`"
          >
            <input
              v-model="standards"
              type="checkbox"
              class="visually-hidden"
              :value="standard"
              @change="handleStandardsChange"
            />
            <span class="choice-check" aria-hidden="true">
              <FontAwesomeIcon icon="check" />
            </span>
            <span class="d-block fw-bold">
              {{ $t(`billing.qualification.standards.${standard}.name`) }}
            </span>
            <!-- Hidden on phones, where two columns leave no room for it; the names are what users look for. -->
            <span class="d-none d-sm-block small text-body-secondary">
              {{
                $t(`billing.qualification.standards.${standard}.description`)
              }}
            </span>
          </label>
        </div>
      </div>
      <input
        v-if="standards.includes('other')"
        v-model="standardOther"
        type="text"
        class="form-control mt-2"
        :maxlength="MAX_STANDARD_OTHER_LENGTH"
        :aria-label="$t('billing.qualification.standards.other.name')"
        :placeholder="$t('billing.qualification.standard_other_placeholder')"
        data-testid="standard-other-input"
        @input="saveStandardOther"
        @blur="saveStandardOther.flush()"
      />
    </fieldset>

    <fieldset class="mb-4" :disabled="loading">
      <legend class="question-label mb-1">
        {{ $t("billing.qualification.regions_label") }}
      </legend>
      <p class="form-text mt-0 mb-2">
        {{ $t("billing.qualification.regions_optional") }}
      </p>
      <div class="d-flex flex-wrap gap-2">
        <label
          v-for="region in CONSUMPTION_REGIONS"
          :key="region"
          class="choice-tile choice-chip"
          :class="{ 'is-selected': regions.includes(region) }"
          :data-testid="`region-${region}`"
        >
          <input
            v-model="regions"
            type="checkbox"
            class="visually-hidden"
            :value="region"
            @change="emitChange({ consumption_regions: orderedRegions })"
          />
          <span class="choice-check" aria-hidden="true">
            <FontAwesomeIcon icon="check" />
          </span>
          <span class="fw-bold">
            {{ $t(`billing.qualification.regions.${region}`) }}
          </span>
        </label>
      </div>
    </fieldset>

    <div
      class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-end gap-2"
    >
      <!-- Repeated by the button: on a phone the unanswered question has usually scrolled out of view. -->
      <span
        v-if="submitted && !isComplete"
        class="text-danger small text-sm-end"
        role="alert"
        data-testid="qualification-incomplete"
      >
        {{ $t("billing.qualification.incomplete") }}
      </span>
      <button
        type="submit"
        class="btn btn-primary btn-lg px-5 submit-button"
        :disabled="loading"
        data-testid="qualification-submit"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ $t("billing.qualification.continue") }}
        <FontAwesomeIcon v-if="!loading" icon="arrow-right" class="ms-2" />
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
/**
 * The four qualification questions. Every answer is emitted as `change` the moment it is given (the free-text
 * "Other" standard after a short pause), so the parent can save it before the user reaches the plan.
 */
import {
  CONSUMPTION_BANDS,
  CONSUMPTION_REGIONS,
  COUNTRY_SCOPES,
  REPORTING_STANDARDS,
  type ConsumptionBand,
  type ConsumptionRegion,
  type CountryScope,
  type QualificationAnswers,
  type QualificationDraft,
  type ReportingStandard,
} from "@/billing/billingTypes";
import { debounce } from "lodash-es";
import { computed, onUnmounted, ref, useId } from "vue";

// Mirrors MAX_REPORTING_STANDARD_OTHER_LENGTH in app/billing/company_billing_service.py.
const MAX_STANDARD_OTHER_LENGTH = 200;
const STANDARD_OTHER_SAVE_DELAY_MS = 600;

const props = withDefaults(
  defineProps<{
    /** Answers saved earlier, e.g. when the user comes back or reopens the dashboard halfway through. */
    initialAnswers?: QualificationDraft;
    loading?: boolean;
  }>(),
  { initialAnswers: () => ({}), loading: false }
);

const emit = defineEmits<{
  change: [draft: QualificationDraft];
  submit: [answers: QualificationAnswers];
}>();

const formId = useId();

const consumptionBand = ref<ConsumptionBand | null>(
  props.initialAnswers.consumption_band ?? null
);
const countryScope = ref<CountryScope | null>(
  props.initialAnswers.country_scope ?? null
);
const standards = ref<ReportingStandard[]>([
  ...(props.initialAnswers.reporting_standards ?? []),
]);
const standardOther = ref(props.initialAnswers.reporting_standard_other ?? "");
const regions = ref<ConsumptionRegion[]>([
  ...(props.initialAnswers.consumption_regions ?? []),
]);
const submitted = ref(false);

const orderedStandards = computed(() =>
  REPORTING_STANDARDS.filter((standard) => standards.value.includes(standard))
);

const orderedRegions = computed(() =>
  CONSUMPTION_REGIONS.filter((region) => regions.value.includes(region))
);

const isComplete = computed(
  () => consumptionBand.value !== null && countryScope.value !== null
);

const saveStandardOther = debounce(
  () => emitChange({ reporting_standard_other: standardOther.value }),
  STANDARD_OTHER_SAVE_DELAY_MS
);

// A pending save still lands when the step is left mid-word.
onUnmounted(() => saveStandardOther.flush());

function emitChange(draft: QualificationDraft) {
  emit("change", draft);
}

// Unticking "Other" drops its text from the saved answers; ticking it again brings the typed text back.
function handleStandardsChange() {
  saveStandardOther.cancel();
  const hasOther = standards.value.includes("other");
  emitChange({
    reporting_standards: orderedStandards.value,
    reporting_standard_other: hasOther ? standardOther.value || null : null,
  });
}

const handleSubmit = () => {
  submitted.value = true;
  if (!consumptionBand.value || !countryScope.value) return;
  // The submit carries the text itself.
  saveStandardOther.cancel();

  emit("submit", {
    consumption_band: consumptionBand.value,
    country_scope: countryScope.value,
    reporting_standards: orderedStandards.value,
    reporting_standard_other: standards.value.includes("other")
      ? standardOther.value
      : null,
    consumption_regions: orderedRegions.value,
  });
};
</script>

<style scoped lang="scss">
.step-eyebrow {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--phoenix-primary);
  margin-bottom: 0.25rem;
}

.question-label {
  font-size: 1rem;
  font-weight: 600;
  color: var(--phoenix-emphasis-color);
}

.choice-tile {
  position: relative;
  display: block;
  cursor: pointer;
  padding: 0.75rem 2.25rem 0.75rem 0.9rem;
  border-radius: 12px;
  border: 1.5px solid var(--phoenix-border-color);
  background: var(--phoenix-body-bg);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: var(--phoenix-primary);
  }

  &:focus-within {
    box-shadow: 0 0 0 0.2rem rgba(var(--phoenix-primary-rgb), 0.25);
  }

  &.is-selected {
    border-color: var(--phoenix-primary);
    background: rgba(var(--phoenix-primary-rgb), 0.06);

    .choice-check {
      background: var(--phoenix-primary);
      border-color: var(--phoenix-primary);
      color: #fff;
    }
  }
}

.submit-button {
  @media (max-width: 575.98px) {
    width: 100%;
  }
}

.choice-chip {
  display: inline-flex;
  align-items: center;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  .choice-check {
    top: 50%;
    transform: translateY(-50%);
  }
}

.choice-check {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  font-size: 0.65rem;
  // Square tick boxes where several answers can be picked, round radio buttons where only one can.
  border-radius: 4px;
  border: 1.5px solid var(--phoenix-border-color);
  color: transparent;

  &.is-radio {
    border-radius: 50%;
  }
}

.is-selected .choice-check.is-radio {
  background: var(--phoenix-body-bg);
  box-shadow:
    inset 0 0 0 3px var(--phoenix-body-bg),
    inset 0 0 0 10px var(--phoenix-primary);
}
</style>
